// ═══════════════════════════════════════════════════════════════════
// Arena cosmetics renderer
// ───────────────────────────────────────────────────────────────────
// Turns a cosmetic's { render_type, asset } into CSS/props. Shared by the
// Shop (previews) and the Arena leaderboard (equipped looks), so an item
// looks identical everywhere. Pure presentation — no game logic.
// ═══════════════════════════════════════════════════════════════════

// Name color / gradient / animated name effect → style object.
export function nameStyle(cosmetic) {
  if (!cosmetic) return {};

  // Animated name effects
  if (cosmetic.render_type === 'css_nameeffect') {
    const a = cosmetic.asset;
    const base = { fontWeight: 800 };
    if (a === 'neon')   return { ...base, color: '#22d3ee', textShadow: '0 0 6px #22d3ee, 0 0 12px #06b6d4', animation: 'cos-neon 1.5s ease-in-out infinite' };
    if (a === 'glow')   return { ...base, color: '#fff', textShadow: '0 0 8px #fbbf24, 0 0 16px #f59e0b' };
    if (a === 'ice')    return { ...base, color: '#bae6fd', textShadow: '0 0 6px #7dd3fc' };
    if (a === 'toxic')  return { ...base, color: '#a3e635', textShadow: '0 0 8px #65a30d, 0 0 14px #4d7c0f', animation: 'cos-neon 2s ease-in-out infinite' };
    if (a === 'flame')  return { ...base, background: 'linear-gradient(0deg,#dc2626,#f97316,#fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textShadow: '0 0 10px rgba(249,115,22,0.6)' };
    if (a === 'gold_shine') return { ...base, background: 'linear-gradient(90deg,#92400e,#fbbf24,#fef3c7,#fbbf24,#92400e)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'cos-prism 2.5s linear infinite' };
    return base;
  }

  // Nameplate → banner behind name (returns text style; banner drawn via class)
  if (cosmetic.render_type === 'css_nameplate') {
    return { color: '#fff', fontWeight: 800, padding: '2px 10px', borderRadius: '8px',
             display: 'inline-block',
             background: cosmetic.asset === 'ocean'
               ? 'linear-gradient(90deg,#0c4a6e,#0891b2)'
               : 'linear-gradient(90deg,#334155,#64748b)' };
  }

  if (cosmetic.render_type !== 'css_namecolor') return {};
  const a = cosmetic.asset;
  if (a === 'fire') {
    return {
      background: 'linear-gradient(90deg,#f97316,#fbbf24,#ef4444)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      backgroundClip: 'text', fontWeight: 800,
    };
  }
  if (a === 'prism') {
    return {
      background: 'linear-gradient(90deg,#f43f5e,#f59e0b,#22c55e,#3b82f6,#a855f7)',
      backgroundSize: '200% auto',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      backgroundClip: 'text', fontWeight: 800,
      animation: 'cos-prism 3s linear infinite',
    };
  }
  return { color: a, fontWeight: 700 };
}

// Frame → className + inline style for the card border.
export function frameProps(cosmetic) {
  if (!cosmetic || cosmetic.render_type !== 'css_frame') return { className: '', style: {} };
  const known = ['bronze','silver','gold','dragon','neon','frost','electric','void','inferno'];
  if (known.includes(cosmetic.asset)) {
    return { className: `cos-frame cos-frame-${cosmetic.asset}` };
  }
  return { className: '', style: {} };
}

// Aura → glow style behind the character. Handles static, animated, rainbow.
export function auraStyle(cosmetic) {
  if (!cosmetic) return null;
  if (cosmetic.render_type === 'css_aura') {
    const c = cosmetic.asset;
    return { background: `radial-gradient(circle, ${c}88 0%, ${c}22 55%, transparent 72%)` };
  }
  if (cosmetic.render_type === 'css_aura_anim') {
    // asset = "color|animname"
    const [c, anim] = cosmetic.asset.split('|');
    const animMap = {
      pulse:   'cos-aura-pulse 1.8s ease-in-out infinite',
      inferno: 'cos-aura-pulse 0.9s ease-in-out infinite',
      void:    'cos-aura-void 3s ease-in-out infinite',
    };
    return {
      background: `radial-gradient(circle, ${c}99 0%, ${c}33 55%, transparent 72%)`,
      animation: animMap[anim] || animMap.pulse,
    };
  }
  if (cosmetic.render_type === 'css_effect' && cosmetic.asset === 'rainbow_aura') {
    return {
      background: 'conic-gradient(#f43f5e,#f59e0b,#22c55e,#3b82f6,#a855f7,#f43f5e)',
      filter: 'blur(14px)', opacity: 0.7,
      animation: 'cos-spin 6s linear infinite',
    };
  }
  return null;
}

// Particle effect kind for a cosmetic (used by shop preview + hero).
export function particleKind(cosmetic) {
  if (cosmetic?.render_type === 'css_particle') return cosmetic.asset; // snow|electric|petals|stars|embers|bubbles
  if (cosmetic?.render_type === 'css_effect')   return cosmetic.asset; // sparks|halo
  return null;
}

// Effect overlay → returns an effect key the component renders (sparks/halo).
export function effectKind(cosmetics) {
  // cosmetics may hold an 'effect' slot and/or an aura that is really an effect
  const e = cosmetics?.effect;
  if (e?.render_type === 'css_effect') return e.asset; // 'sparks' | 'halo' | 'rainbow_aura'
  return null;
}

export function titleText(cosmetics) {
  const t = cosmetics?.title;
  if (t?.render_type === 'text_title') return t.asset;
  return null;
}

export function badgeEmoji(cosmetics) {
  const b = cosmetics?.badge;
  if (b?.render_type === 'emoji_badge') return b.asset;
  return null;
}

export const RARITY_COLORS = {
  common:    { label: 'Common',    color: '#94a3b8', glow: 'rgba(148,163,184,0.3)' },
  rare:      { label: 'Rare',      color: '#3b82f6', glow: 'rgba(59,130,246,0.4)' },
  epic:      { label: 'Epic',      color: '#a855f7', glow: 'rgba(168,85,247,0.45)' },
  legendary: { label: 'Legendary', color: '#f59e0b', glow: 'rgba(245,158,11,0.5)' },
};